import type { DataProvider, RaRecord } from 'react-admin';

import {
    createAdminBlog,
    deleteAdminBlog,
    getAdminBlogList,
    getAdminBlogOne,
    getManyAdminBlog,
    isAdminBlogResource,
    updateAdminBlog,
} from './adminBlogDataProvider';
import {
    createExamTask,
    deleteExamTask,
    EXAM_TASK_DESTINATION_RESOURCE,
    EXAM_TASK_REVIEW_RESOURCE,
    getExamTask,
    getExamTaskList,
    getManyExamTasks,
    updateExamTask,
} from './examTaskReviewDataProvider';
import {
    createAdminSubscription,
    deleteAdminSubscription,
    getAdminSubscriptionList,
    getAdminSubscriptionOne,
    getManyAdminSubscription,
    isAdminSubscriptionResource,
    updateAdminSubscription,
} from './adminSubscriptionDataProvider';
import {
    deleteAdminContactUs,
    getAdminContactUsList,
    getAdminContactUsOne,
    getManyAdminContactUs,
    isAdminContactUsResource,
    updateAdminContactUs,
} from './adminContactUsDataProvider';
import {
    deleteAdminFeedback,
    getAdminFeedbackList,
    getAdminFeedbackOne,
    getManyAdminFeedback,
    isAdminFeedbackResource,
    updateAdminFeedback,
} from './adminFeedbackDataProvider';
import {
    deleteAdminAiScoreFeedback,
    getAdminAiScoreFeedbackList,
    getAdminAiScoreFeedbackOne,
    getManyAdminAiScoreFeedback,
    isAdminAiScoreFeedbackResource,
    updateAdminAiScoreFeedback,
} from './adminAiScoreFeedbackDataProvider';
import {
    createAdminUser,
    deleteAdminUser,
    getAdminUsersList,
    getAdminUsersOne,
    getManyAdminUsers,
    isAdminUsersResource,
    updateAdminUser,
} from './adminUserDataProvider';

const STORAGE_PREFIX = 'pteultra-admin:';

function safeJsonParse<T>(raw: string | null): T | null {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

function readCollection<T extends RaRecord>(resource: string, fallback: T[]): T[] {
    const key = `${STORAGE_PREFIX}${resource}`;
    const parsed = safeJsonParse<T[]>(localStorage.getItem(key));
    if (!parsed) {
        localStorage.setItem(key, JSON.stringify(fallback));
        return fallback;
    }
    return parsed;
}

function writeCollection<T extends RaRecord>(resource: string, data: T[]): void {
    const key = `${STORAGE_PREFIX}${resource}`;
    localStorage.setItem(key, JSON.stringify(data));
}

function matchesFilter(record: Record<string, unknown>, filter: Record<string, unknown>): boolean {
    const entries = Object.entries(filter ?? {});
    if (entries.length === 0) return true;

    return entries.every(([key, value]) => {
        if (value === undefined || value === null || value === '') return true;
        const recordValue = record[key];
        if (recordValue === undefined || recordValue === null) return false;
        return String(recordValue).toLowerCase().includes(String(value).toLowerCase());
    });
}

function compareValues(a: unknown, b: unknown): number {
    if (a === b) return 0;
    if (a === undefined || a === null) return -1;
    if (b === undefined || b === null) return 1;

    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

function getNextNumericId(records: Array<{ id: number }>): number {
    const maxId = records.reduce((acc, r) => (r.id > acc ? r.id : acc), 0);
    return maxId + 1;
}

function getResourceData(resource: string): RaRecord[] {
    switch (resource) {
        default:
            return readCollection<RaRecord>(resource, []);
    }
}

function setResourceData(resource: string, data: RaRecord[]): void {
    writeCollection(resource, data);
}

export const dataProvider = {
    async getList(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            return getAdminBlogList(params);
        }

        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return getExamTaskList(resource, params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return getAdminSubscriptionList(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            return getAdminContactUsList(params);
        }

        if (isAdminFeedbackResource(resource)) {
            return getAdminFeedbackList(params);
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            return getAdminAiScoreFeedbackList(params);
        }

        if (isAdminUsersResource(resource)) {
            return getAdminUsersList(params);
        }

        const collection = getResourceData(resource);
        const filtered = collection.filter((r) => matchesFilter(r as Record<string, unknown>, params.filter));

        const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };
        const sorted = [...filtered].sort((a, b) => {
            const cmp = compareValues((a as any)[field], (b as any)[field]);
            return order === 'DESC' ? -cmp : cmp;
        });

        const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
        const start = (page - 1) * perPage;
        const end = start + perPage;
        const data = sorted.slice(start, end);

        return { data: data as any, total: sorted.length };
    },

    async getOne(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            return getAdminBlogOne(params);
        }

        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return getExamTask(resource, params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return getAdminSubscriptionOne(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            return getAdminContactUsOne(params);
        }

        if (isAdminFeedbackResource(resource)) {
            return getAdminFeedbackOne(params);
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            return getAdminAiScoreFeedbackOne(params);
        }

        if (isAdminUsersResource(resource)) {
            return getAdminUsersOne(params);
        }

        const collection = getResourceData(resource);
        const record = collection.find((r) => r.id === params.id);
        if (!record) throw new Error(`Record not found: ${resource}#${params.id}`);
        return { data: record as any };
    },

    async getMany(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            return getManyAdminBlog(params);
        }

        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return getManyExamTasks(resource, params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return getManyAdminSubscription(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            return getManyAdminContactUs(params);
        }

        if (isAdminFeedbackResource(resource)) {
            return getManyAdminFeedback(params);
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            return getManyAdminAiScoreFeedback(params);
        }

        if (isAdminUsersResource(resource)) {
            return getManyAdminUsers(params);
        }

        const collection = getResourceData(resource);
        const ids = new Set(params.ids);
        const data = collection.filter((r) => ids.has(r.id));
        return { data: data as any };
    },

    async getManyReference(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            return getAdminBlogList(params);
        }

        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return getExamTaskList(resource, params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return getAdminSubscriptionList(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            return getAdminContactUsList(params);
        }

        if (isAdminFeedbackResource(resource)) {
            return getAdminFeedbackList(params);
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            return getAdminAiScoreFeedbackList(params);
        }

        if (isAdminUsersResource(resource)) {
            return getAdminUsersList(params);
        }

        const collection = getResourceData(resource);
        const filtered = collection.filter((r) => (r as any)[params.target] === params.id);

        const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };
        const sorted = [...filtered].sort((a, b) => {
            const cmp = compareValues((a as any)[field], (b as any)[field]);
            return order === 'DESC' ? -cmp : cmp;
        });

        const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
        const start = (page - 1) * perPage;
        const end = start + perPage;
        const data = sorted.slice(start, end);

        return { data: data as any, total: sorted.length };
    },

    async create(resource: string, params: any) {
        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return createExamTask(resource, params);
        }

        if (isAdminBlogResource(resource)) {
            return createAdminBlog(params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return createAdminSubscription(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            throw new Error('Creating contact requests is not supported.');
        }

        if (isAdminFeedbackResource(resource)) {
            throw new Error('Creating feedback is not supported.');
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            throw new Error('Creating AI score feedback is not supported.');
        }

        if (isAdminUsersResource(resource)) {
            return createAdminUser(params);
        }

        const collection = getResourceData(resource);

        const nowIso = new Date().toISOString();
        const newRecord: RaRecord = {
            ...params.data,
            id: getNextNumericId(collection as Array<{ id: number }>),
            updatedAt: (params.data as any).updatedAt ?? nowIso,
        };

        const next = [newRecord, ...collection];
        setResourceData(resource, next);
        return { data: newRecord as any };
    },

    async update(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            return updateAdminBlog(params);
        }

        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return updateExamTask(resource, params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return updateAdminSubscription(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            return updateAdminContactUs(params);
        }

        if (isAdminFeedbackResource(resource)) {
            return updateAdminFeedback(params);
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            return updateAdminAiScoreFeedback(params);
        }

        if (isAdminUsersResource(resource)) {
            return updateAdminUser(params);
        }

        const collection = getResourceData(resource);
        const nowIso = new Date().toISOString();

        const next = collection.map((r) => {
            if (r.id !== params.id) return r;
            return {
                ...r,
                ...params.data,
                updatedAt: (params.data as any).updatedAt ?? nowIso,
            };
        });

        setResourceData(resource, next);
        const updated = next.find((r) => r.id === params.id);
        if (!updated) throw new Error(`Record not found: ${resource}#${params.id}`);
        return { data: updated as any };
    },

    async updateMany(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    updateAdminBlog({
                        ...params,
                        id,
                        data: {
                            ...params.data,
                        },
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminContactUsResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    updateAdminContactUs({
                        ...params,
                        id,
                        data: {
                            ...params.data,
                        },
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminFeedbackResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    updateAdminFeedback({
                        ...params,
                        id,
                        data: {
                            ...params.data,
                        },
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    updateAdminAiScoreFeedback({
                        ...params,
                        id,
                        data: {
                            ...params.data,
                        },
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminUsersResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    updateAdminUser({
                        ...params,
                        id,
                        data: {
                            ...params.data,
                        },
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        const collection = getResourceData(resource);
        const ids = new Set(params.ids);
        const nowIso = new Date().toISOString();

        const next = collection.map((r) => {
            if (!ids.has(r.id)) return r;
            return {
                ...r,
                ...params.data,
                updatedAt: (params.data as any).updatedAt ?? nowIso,
            };
        });

        setResourceData(resource, next);
        return { data: params.ids as any };
    },

    async delete(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            return deleteAdminBlog(params);
        }

        if (resource === EXAM_TASK_REVIEW_RESOURCE || resource === EXAM_TASK_DESTINATION_RESOURCE) {
            return deleteExamTask(resource, params);
        }

        if (isAdminSubscriptionResource(resource)) {
            return deleteAdminSubscription(resource, params);
        }

        if (isAdminContactUsResource(resource)) {
            return deleteAdminContactUs(params);
        }

        if (isAdminFeedbackResource(resource)) {
            return deleteAdminFeedback(params);
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            return deleteAdminAiScoreFeedback(params);
        }

        if (isAdminUsersResource(resource)) {
            return deleteAdminUser(params);
        }

        const collection = getResourceData(resource);
        const record = collection.find((r) => r.id === params.id);
        const next = collection.filter((r) => r.id !== params.id);
        setResourceData(resource, next);

        if (!record) throw new Error(`Record not found: ${resource}#${params.id}`);
        return { data: record as any };
    },

    async deleteMany(resource: string, params: any) {
        if (isAdminBlogResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    deleteAdminBlog({
                        ...params,
                        id,
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminContactUsResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    deleteAdminContactUs({
                        ...params,
                        id,
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminFeedbackResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    deleteAdminFeedback({
                        ...params,
                        id,
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminAiScoreFeedbackResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    deleteAdminAiScoreFeedback({
                        ...params,
                        id,
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        if (isAdminUsersResource(resource)) {
            const results = await Promise.all(
                params.ids.map((id: unknown) =>
                    deleteAdminUser({
                        ...params,
                        id,
                    }),
                ),
            );

            return { data: results.map((result) => result.data.id) as any };
        }

        const collection = getResourceData(resource);
        const ids = new Set(params.ids);
        const next = collection.filter((r) => !ids.has(r.id));
        setResourceData(resource, next);
        return { data: params.ids as any };
    },
    } as unknown as DataProvider;
